import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { LK_CATEGORY_LEFT_COLORS, LK_PERIOD_COLORS } from '../../data/legalKnowledge';
import {
  fetchDashboardByCategory,
  fetchDashboardByPeriod,
  fetchDashboardStatistics,
  LkaDashboardByCategoryResponse,
  LkaDashboardByPeriodResponse,
  LkaDashboardStatisticsResponse,
} from '../../services/legalKnowledgeArchive';

function Tooltip({ text, children }: { text: string; children: React.ReactElement<React.HTMLAttributes<HTMLElement>> }) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const child = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setPos({ x: r.left, y: r.top });
    },
    onMouseLeave: () => setPos(null),
  });

  return (
    <>
      {child}
      {pos &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: pos.y - 8,
              left: pos.x,
              transform: 'translateY(-100%)',
              background: 'rgba(0,0,0,.78)',
              color: '#fff',
              fontSize: 11,
              padding: '4px 8px',
              borderRadius: 4,
              zIndex: 9999,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}

const ROW_BTN = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  background: 'none',
  border: 'none',
  padding: '6px 0',
  cursor: 'pointer',
  textAlign: 'left' as const,
  position: 'relative' as const,
} as const;

function ProgressBar({ pct, color, height = 7 }: { pct: number; color: string; height?: number }) {
  return (
    <div style={{ background: 'var(--bl)', borderRadius: 4, height, overflow: 'hidden', marginTop: 4 }}>
      <div style={{ background: color, width: `${Math.min(pct, 100)}%`, height: '100%', borderRadius: 4 }} />
    </div>
  );
}

function SubProgressBar({
  subCount,
  parentCount,
  parentPct,
  color,
  height = 5,
  trackOffset = 28,
}: {
  subCount: number;
  parentCount: number;
  parentPct: number;
  color: string;
  height?: number;
  trackOffset?: number;
}) {
  const fillWithinParent = parentCount > 0 ? Math.min((subCount / parentCount) * 100, 100) : 0;
  return (
    <div style={{ marginTop: 4 }}>
      <div
        style={{
          width: `calc(${parentPct}% - ${trackOffset}px)`,
          height,
          background: 'var(--bl)',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${fillWithinParent}%`, height: '100%', background: color, borderRadius: 4 }} />
      </div>
    </div>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points={expanded ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--t3)" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

const CARD_STYLE = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rs)',
  padding: '20px 24px',
} as const;

function KpiCard({
  label,
  labelColor,
  count,
  description,
  todayLabel,
}: {
  label: string;
  labelColor: string;
  count: number;
  description: string;
  todayLabel: string;
}) {
  return (
    <div style={{ ...CARD_STYLE, flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ color: labelColor, fontSize: 13, fontWeight: 700 }}>{label}</span>
        <span style={{ fontSize: 32, fontWeight: 800, color: 'var(--text)' }}>{count.toLocaleString()}</span>
      </div>
      <div style={{ marginTop: 12, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <InfoIcon />
        <span style={{ fontSize: 11, color: 'var(--t3)', lineHeight: 1.5 }}>
          {description}{' '}
          <span style={{ color: 'var(--blue, #3b82f6)', fontWeight: 600 }}>{todayLabel}</span>
        </span>
      </div>
    </div>
  );
}

const VLINE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: 6,
  background: 'var(--border)',
  borderRadius: 3,
  transform: 'translateX(-5px)',
};

const VLINE_SUB: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: 4,
  background: 'var(--border)',
  borderRadius: 2,
  transform: 'translateX(-3px)',
};

export default function LKADashboardPage() {
  const { t } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [expandedPeriodCategories, setExpandedPeriodCategories] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<LkaDashboardStatisticsResponse | null>(null);
  const [categoryData, setCategoryData] = useState<LkaDashboardByCategoryResponse | null>(null);
  const [periodData, setPeriodData] = useState<LkaDashboardByPeriodResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    Promise.all([fetchDashboardStatistics(), fetchDashboardByCategory(), fetchDashboardByPeriod()])
      .then(([s, c, p]) => { setStats(s); setCategoryData(c); setPeriodData(p); })
      .catch(() => setError(t('legalKnowledge.dashboardPage.loadFailed')))
      .finally(() => setLoading(false));
  }, [t]);

  const categories = categoryData?.categories ?? [];
  const periods = periodData?.periods ?? [];
  const maxCategoryCount = Math.max(1, ...categories.map((c) => c.total_count));
  const maxPeriodCount = Math.max(1, ...periods.map((m) => m.count));
  const asOfFormatted = dayjs().format('DD/MM/YYYY');
  const todayLabel = t('legalKnowledge.dashboardPage.today', { date: asOfFormatted });

  const toggleCategory = (id: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleMonth = (mo: string) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(mo)) next.delete(mo);
      else next.add(mo);
      return next;
    });
  };

  const togglePeriodCategory = (key: string) => {
    setExpandedPeriodCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.legalKnowledgeArchiveManagement')}</span>
        <span>/</span>
        <span>{t('legalKnowledge.dashboardPage.title')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('legalKnowledge.dashboardPage.title')}</div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 32, color: 'var(--t3)' }}>{t('legalKnowledge.dashboardPage.loading')}</div>
      ) : error ? (
        <div style={{ padding: 32, color: 'var(--red)' }}>{error}</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            <KpiCard
              label={t('legalKnowledge.dashboardPage.totalPublished')}
              labelColor="#22c55e"
              count={stats?.total_published ?? 0}
              description={t('legalKnowledge.dashboardPage.totalPublishedDesc')}
              todayLabel={todayLabel}
            />
            <KpiCard
              label={t('legalKnowledge.dashboardPage.drafts')}
              labelColor="#f59e0b"
              count={stats?.draft_count ?? 0}
              description={t('legalKnowledge.dashboardPage.draftsDesc')}
              todayLabel={todayLabel}
            />
            <KpiCard
              label={t('legalKnowledge.dashboardPage.rejected')}
              labelColor="#ef4444"
              count={stats?.rejected_count ?? 0}
              description={t('legalKnowledge.dashboardPage.rejectedDesc')}
              todayLabel={todayLabel}
            />
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ ...CARD_STYLE, flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
                {t('legalKnowledge.dashboardPage.byCategoryTitle')}
              </div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start', marginBottom: 16 }}>
                <InfoIcon />
                <span style={{ fontSize: 11, color: 'var(--t3)' }}>
                  {t('legalKnowledge.dashboardPage.byCategoryDesc')}{' '}
                  <span style={{ color: 'var(--blue, #3b82f6)', fontWeight: 600 }}>{todayLabel}</span>
                </span>
              </div>

              <div style={{ maxHeight: 420, overflowY: 'auto', padding: '0 8px 0 0' }}>
                {categories.map((cat, i) => {
                  const expanded = expandedCategories.has(cat.file_category_name);
                  const pct = (cat.total_count / maxCategoryCount) * 100;
                  const color = LK_CATEGORY_LEFT_COLORS[i % LK_CATEGORY_LEFT_COLORS.length];

                  return (
                    <div key={cat.file_category_name} style={{ paddingTop: i === 0 ? 0 : 22 }}>
                      <button
                        type="button"
                        style={ROW_BTN}
                        onClick={() => toggleCategory(cat.file_category_name)}
                      >
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                          {cat.file_category_name}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginRight: 10 }}>
                          {cat.total_count.toLocaleString()}
                        </span>
                        <span style={{ color: 'var(--t3)', display: 'flex', flexShrink: 0 }}>
                          <ChevronIcon expanded={expanded} />
                        </span>
                      </button>
                      <ProgressBar pct={pct} color={color} height={9} />

                      {expanded && (
                        <div style={{ marginTop: 16, position: 'relative', zIndex: 1 }}>
                          <div style={{ ...VLINE, left: `${pct}%` }} />
                          {cat.sub_categories.map((sub) => {
                            return (
                              <div key={sub.sub_file_category_name} style={{ marginTop: 16 }}>
                                <div style={{ position: 'relative' }}>
                                  <Tooltip text={sub.sub_file_category_name}>
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        maxWidth: `calc(${pct}% - 65px)`,
                                        verticalAlign: 'bottom',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: 12,
                                        color: 'var(--t2)',
                                      }}
                                    >
                                      {sub.sub_file_category_name}
                                    </span>
                                  </Tooltip>
                                  <Tooltip text={sub.sub_file_category_name}>
                                    <span
                                      style={{
                                        position: 'absolute',
                                        left: `calc(${pct}% - 30px)`,
                                        top: 0,
                                        transform: 'translateX(-100%)',
                                        fontSize: 12,
                                        color: 'var(--t2)',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {sub.count.toLocaleString()}
                                    </span>
                                  </Tooltip>
                                </div>
                                <SubProgressBar
                                  subCount={sub.count}
                                  parentCount={cat.total_count}
                                  parentPct={pct}
                                  color={color}
                                  height={6}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ ...CARD_STYLE, flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t('legalKnowledge.dashboardPage.byPeriodTitle')}</div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start', marginBottom: 16 }}>
                <InfoIcon />
                <span style={{ fontSize: 11, color: 'var(--t3)' }}>
                  {t('legalKnowledge.dashboardPage.byPeriodDesc')}{' '}
                  <span style={{ color: 'var(--blue, #3b82f6)', fontWeight: 600 }}>{todayLabel}</span>
                </span>
              </div>

              <div style={{ maxHeight: 420, overflowY: 'auto', padding: '0 8px 0 0' }}>
                {periods.map((mo, i) => {
                  const moExpanded = expandedMonths.has(mo.period_label);
                  const moPct = (mo.count / maxPeriodCount) * 100;
                  const periodColor = LK_PERIOD_COLORS[i % LK_PERIOD_COLORS.length];

                  return (
                    <div key={mo.period_label} style={{ paddingTop: i === 0 ? 0 : 22 }}>
                      <button type="button" style={ROW_BTN} onClick={() => toggleMonth(mo.period_label)}>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                          {mo.period_label}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginRight: 10 }}>
                          {mo.count.toLocaleString()}
                        </span>
                        <span style={{ color: 'var(--t3)', display: 'flex', flexShrink: 0 }}>
                          <ChevronIcon expanded={moExpanded} />
                        </span>
                      </button>
                      <ProgressBar pct={moPct} color={periodColor} height={9} />

                      {moExpanded && (
                        <div style={{ marginTop: 16, position: 'relative', zIndex: 1 }}>
                          <div style={{ ...VLINE, left: `${moPct}%` }} />
                          {mo.categories.map((cat) => {
                            const catKey = `${mo.period_label}__${cat.file_category_name}`;
                            const catExpanded = expandedPeriodCategories.has(catKey);
                            const catColor = periodColor;
                            const hasSubs = cat.sub_categories && cat.sub_categories.length > 0;
                            const catPct = (cat.count / mo.count) * moPct;
                            const subTrackOffset = (cat.count / mo.count) * 28 + 10;
                            return (
                              <div key={cat.file_category_name} style={{ marginTop: 16 }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    cursor: hasSubs ? 'pointer' : 'default',
                                  }}
                                  onClick={hasSubs ? () => togglePeriodCategory(catKey) : undefined}
                                >
                                  <Tooltip text={cat.file_category_name}>
                                    <span
                                      style={{
                                        flex: 1,
                                        display: 'inline-block',
                                        maxWidth: `calc(${moPct}% - ${hasSubs ? 65 : 47}px)`,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: 12,
                                        color: 'var(--t2)',
                                      }}
                                    >
                                      {cat.file_category_name}
                                    </span>
                                  </Tooltip>
                                  <Tooltip text={cat.file_category_name}>
                                    <span
                                      style={{
                                        fontSize: 12,
                                        color: 'var(--t2)',
                                        whiteSpace: 'nowrap',
                                        marginLeft: 8,
                                        marginRight: hasSubs ? 4 : 0,
                                      }}
                                    >
                                      {cat.count.toLocaleString()}
                                    </span>
                                  </Tooltip>
                                  {hasSubs && (
                                    <span style={{ color: 'var(--t3)', display: 'flex', flexShrink: 0 }}>
                                      <ChevronIcon expanded={catExpanded} />
                                    </span>
                                  )}
                                </div>
                                <SubProgressBar
                                  subCount={cat.count}
                                  parentCount={mo.count}
                                  parentPct={moPct}
                                  color={catColor}
                                  height={6}
                                />
                                {catExpanded && hasSubs && (
                                  <div style={{ marginTop: 12, position: 'relative' }}>
                                    <div style={{ ...VLINE_SUB, left: `calc(${catPct}% - ${(cat.count / mo.count * 28).toFixed(1)}px)` }} />
                                    {cat.sub_categories.map((sub) => (
                                      <div key={sub.sub_file_category_name} style={{ marginTop: 14 }}>
                                        <div style={{ position: 'relative' }}>
                                          <Tooltip text={sub.sub_file_category_name}>
                                            <span
                                              style={{
                                                display: 'inline-block',
                                                maxWidth: `calc(${catPct}% - 65px)`,
                                                verticalAlign: 'bottom',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: 11,
                                                color: 'var(--t2)',
                                              }}
                                            >
                                              {sub.sub_file_category_name}
                                            </span>
                                          </Tooltip>
                                          <Tooltip text={sub.sub_file_category_name}>
                                            <span
                                              style={{
                                                position: 'absolute',
                                                left: `calc(${catPct}% - ${subTrackOffset.toFixed(1)}px)`,
                                                top: 0,
                                                transform: 'translateX(-100%)',
                                                fontSize: 11,
                                                fontWeight: 600,
                                                color: 'var(--t2)',
                                                whiteSpace: 'nowrap',
                                              }}
                                            >
                                              {sub.count.toLocaleString()}
                                            </span>
                                          </Tooltip>
                                        </div>
                                        <SubProgressBar
                                          subCount={sub.count}
                                          parentCount={cat.count}
                                          parentPct={catPct}
                                          color={catColor}
                                          height={4}
                                          trackOffset={subTrackOffset}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
